import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useAppDispatch, useAppSelector } from "../../redux/store"
import { setSearchValue } from "../../redux/slices/events/eventsSlice"
import { TabId } from "./useEventGroups"
import {
  EventsSortField,
  EventsSortDir,
} from "../../components/Events/types"
import styles from "./EventsPage.module.scss"

interface EventsToolbarProps {
  tab: TabId
  onTabChange: (tab: TabId) => void
  tabCounts: Record<TabId, number>
  // Server-reported total used as the authoritative "All" chip count so
  // it never disagrees with the header subtitle. The per-bucket counts
  // stay client-derived — the backend doesn't break them down per tab,
  // and they converge to the true values once pagination fully drains.
  totalAll: number
  sortField: EventsSortField
  sortDir: EventsSortDir
  onSortChange: (field: EventsSortField, dir: EventsSortDir) => void
}

const SearchIcon = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <circle cx="6.5" cy="6.5" r="4" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M10 10L12 12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
)

const ChevronDownIcon = () => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
    <path
      d="M3 4.5L6 7.5L9 4.5"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

// Keep tab order deliberately: urgency descending. `All` sits first as the
// default resting state; the narrower filters follow in importance order.
const TABS: { id: TabId; labelKey: string; fallback: string }[] = [
  { id: "all", labelKey: "events.tabs.all", fallback: "All" },
  { id: "upcoming", labelKey: "events.tabs.upcoming", fallback: "Upcoming" },
  { id: "drafts", labelKey: "events.tabs.drafts", fallback: "Drafts" },
  { id: "past", labelKey: "events.tabs.past", fallback: "Past" },
]

// Two-axis sort. Each option maps to a unique (field, direction) pair so
// the backend request is unambiguous, and the composite `id` lets the
// popover keep simple string equality for the selected item. Field order
// below ("created" first) is intentional — users think in creation order
// far more often than event order on this screen.
type SortOption = {
  id: string
  field: EventsSortField
  dir: EventsSortDir
  labelKey: string
  fallback: string
}

const SORT_OPTIONS: SortOption[] = [
  {
    id: "event_created_date:desc",
    field: "event_created_date",
    dir: "desc",
    labelKey: "events.sort.createdDesc",
    fallback: "Date created · newest first",
  },
  {
    id: "event_created_date:asc",
    field: "event_created_date",
    dir: "asc",
    labelKey: "events.sort.createdAsc",
    fallback: "Date created · oldest first",
  },
  {
    id: "start_datetime:desc",
    field: "start_datetime",
    dir: "desc",
    labelKey: "events.sort.eventDateDesc",
    fallback: "Event date · newest first",
  },
  {
    id: "start_datetime:asc",
    field: "start_datetime",
    dir: "asc",
    labelKey: "events.sort.eventDateAsc",
    fallback: "Event date · oldest first",
  },
]

const sortOptionId = (field: EventsSortField, dir: EventsSortDir): string =>
  `${field}:${dir}`

const EventsToolbar = ({
  tab,
  onTabChange,
  tabCounts,
  totalAll,
  sortField,
  sortDir,
  onSortChange,
}: EventsToolbarProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { search_value } = useAppSelector((state) => state.events)
  const inputRef = useRef<HTMLInputElement>(null)
  const [sortOpen, setSortOpen] = useState(false)
  const sortRef = useRef<HTMLDivElement>(null)
  const tabsRef = useRef<HTMLDivElement>(null)
  // Track which side of the tab strip has hidden content. The edge-fade
  // overlays in SCSS turn on via the `tabsContainerOverflowLeft/Right`
  // classes, so users get a visual cue that the strip is scrollable
  // instead of a silently-hidden scrollbar.
  const [tabsOverflow, setTabsOverflow] = useState({
    left: false,
    right: false,
  })

  // "/" anywhere on the page focuses search (Stripe convention). We skip it
  // if the user is already typing in another input so it doesn't hijack.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/") return
      const active = document.activeElement
      const isTyping =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        (active instanceof HTMLElement && active.isContentEditable)
      if (isTyping) return
      e.preventDefault()
      inputRef.current?.focus()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  // Keep the active tab visible when it changes (URL state, keyboard,
  // programmatic tab-switch). Without this, switching to a tab that's
  // off-screen leaves the active underline hidden on narrow viewports.
  useEffect(() => {
    const strip = tabsRef.current
    if (!strip) return
    const active = strip.querySelector<HTMLButtonElement>(
      "[aria-selected='true']",
    )
    if (active) {
      active.scrollIntoView({ block: "nearest", inline: "nearest" })
    }
  }, [tab])

  // Track overflow state on both edges. We read layout once on mount,
  // then on every scroll and whenever the strip or viewport resizes.
  // ResizeObserver catches layout changes that a `resize` listener
  // misses (font load, sidebar open/close, parent width shifts).
  useEffect(() => {
    const strip = tabsRef.current
    if (!strip) return

    const measure = () => {
      const { scrollLeft, scrollWidth, clientWidth } = strip
      const left = scrollLeft > 1
      const right = scrollLeft + clientWidth < scrollWidth - 1
      setTabsOverflow((prev) =>
        prev.left === left && prev.right === right ? prev : { left, right },
      )
    }

    measure()
    strip.addEventListener("scroll", measure, { passive: true })
    const ro = new ResizeObserver(measure)
    ro.observe(strip)
    return () => {
      strip.removeEventListener("scroll", measure)
      ro.disconnect()
    }
  }, [])

  // Close the sort menu when clicking outside so it behaves like a proper
  // popover rather than a sticky dropdown.
  useEffect(() => {
    if (!sortOpen) return
    const onClick = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
        setSortOpen(false)
      }
    }
    window.addEventListener("mousedown", onClick)
    return () => window.removeEventListener("mousedown", onClick)
  }, [sortOpen])

  const activeSortId = sortOptionId(sortField, sortDir)
  const activeSortOption =
    SORT_OPTIONS.find((o) => o.id === activeSortId) ?? SORT_OPTIONS[0]
  const currentSortLabel = t(
    activeSortOption.labelKey,
    activeSortOption.fallback,
  )

  const tabsContainerClass = [
    styles.tabsContainer,
    tabsOverflow.left ? styles.tabsContainerOverflowLeft : "",
    tabsOverflow.right ? styles.tabsContainerOverflowRight : "",
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <div className={styles.toolbar}>
      <div className={tabsContainerClass}>
        <div className={styles.tabs} ref={tabsRef} role="tablist">
          {TABS.map((tabDef) => {
            // "All" uses the server total so the chip matches the header
            // from the very first render. Other tabs stay client-derived
            // because the backend doesn't segment totals by lifecycle;
            // they converge to correct values as auto-pagination fills
            // the in-memory accumulator.
            const count =
              tabDef.id === "all" ? totalAll : tabCounts[tabDef.id]
            const isActive = tab === tabDef.id
            return (
              <button
                key={tabDef.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`${styles.tab} ${
                  isActive ? styles.tabActive : ""
                }`}
                onClick={() => onTabChange(tabDef.id)}
              >
                {t(tabDef.labelKey, tabDef.fallback)}
                {count > 0 && (
                  <span className={styles.tabCount}>{count}</span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      <div className={styles.toolbarRight}>
        <div className={styles.search}>
          <SearchIcon />
          <input
            ref={inputRef}
            type="text"
            placeholder={t("placeholder.search")}
            value={search_value}
            onChange={(e) => dispatch(setSearchValue(e.target.value))}
          />
        </div>

        <div ref={sortRef} className={styles.sortWrap}>
          <button
            type="button"
            className={`${styles.ghostBtn} ${
              sortOpen ? styles.ghostBtnActive : ""
            }`}
            onClick={() => setSortOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={sortOpen}
          >
            {currentSortLabel}
            <ChevronDownIcon />
          </button>
          {sortOpen && (
            <SortMenu
              value={activeSortId}
              options={SORT_OPTIONS.map((o) => ({
                id: o.id,
                label: t(o.labelKey, o.fallback),
              }))}
              onSelect={(id) => {
                const picked = SORT_OPTIONS.find((o) => o.id === id)
                if (picked) onSortChange(picked.field, picked.dir)
                setSortOpen(false)
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// Small inline popover. Intentionally not a react-bootstrap dropdown to keep
// the toolbar's visual weight low and avoid the heavy library styling.
const SortMenu = ({
  value,
  options,
  onSelect,
}: {
  value: string
  options: { id: string; label: string }[]
  onSelect: (id: string) => void
}) => (
  <div
    role="listbox"
    style={{
      position: "absolute",
      top: "calc(100% + 4px)",
      right: 0,
      minWidth: 240,
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: 8,
      boxShadow: "0 4px 16px rgba(15,23,42,0.08), 0 1px 4px rgba(15,23,42,0.04)",
      padding: 4,
      zIndex: 10,
    }}
  >
    {options.map((o) => {
      const isActive = o.id === value
      return (
        <button
          key={o.id}
          role="option"
          aria-selected={isActive}
          type="button"
          onClick={() => onSelect(o.id)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            padding: "6px 10px",
            background: isActive ? "#f6f8fa" : "transparent",
            border: 0,
            borderRadius: 6,
            color: "#0f172a",
            fontSize: 13,
            fontWeight: isActive ? 500 : 400,
            cursor: "pointer",
            textAlign: "left",
          }}
          onMouseEnter={(e) => {
            if (!isActive) e.currentTarget.style.background = "#f6f8fa"
          }}
          onMouseLeave={(e) => {
            if (!isActive) e.currentTarget.style.background = "transparent"
          }}
        >
          {o.label}
          {isActive && (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M2.5 6.5L5 9L9.5 3.5"
                stroke="#0f172a"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </button>
      )
    })}
  </div>
)

export default EventsToolbar
